import React, { useMemo, useState } from 'react';
import { ArrowDownWideNarrow, ArrowUpWideNarrow } from 'lucide-react';

const THEMES = {
  blue: {
    accent: '#3b8df8',
    accent2: '#74b3ff',
    wash: '#edf5ff',
    icon: '↗',
  },
  green: {
    accent: '#16b981',
    accent2: '#55d6ad',
    wash: '#ecfaf5',
    icon: '◇',
  },
  purple: {
    accent: '#8055ed',
    accent2: '#ad8aff',
    wash: '#f4efff',
    icon: '◈',
  },
};

/**
 * Ranked horizontal bar list.
 * variant="card" — Promo Deals modern chart cards
 * variant="inline" — compact list for Overview panels
 */
const ScrollableBarList = ({
  title = 'Ranking',
  footerLabel = 'Metric',
  footerStrong,
  rows = [],
  nameKey = 'name',
  valueKey = 'value',
  formatValue,
  theme = 'blue',
  barColor,
  emptyText = 'No data in this range.',
  defaultSort = 'desc',
  maxVisible = 6,
  variant = 'card',
  className = '',
}) => {
  const [sortDir, setSortDir] = useState(defaultSort);
  const palette = THEMES[theme] || THEMES.blue;
  const accent = barColor || palette.accent;
  const accent2 = barColor ? barColor : palette.accent2;
  const wash = palette.wash;

  const sorted = useMemo(() => {
    const next = [...(rows || [])];
    next.sort((a, b) => {
      const av = Number(a?.[valueKey] || 0);
      const bv = Number(b?.[valueKey] || 0);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return next;
  }, [rows, valueKey, sortDir]);

  const maxValue = Math.max(...sorted.map((row) => Number(row?.[valueKey] || 0)), 1);
  const itemLabel = sorted.length === 1 ? 'item' : 'items';

  const rowsBlock = !sorted.length ? (
    <p className={`py-8 text-center text-sm text-[#98a3b5] ${variant === 'inline' ? 'text-gray-500' : ''}`}>
      {emptyText}
    </p>
  ) : (
    <div className={`bi-slim-scroll h-full overflow-y-auto overflow-x-hidden pr-1 ${variant === 'card' ? '' : ''}`}>
      {sorted.map((row, index) => {
        const value = Number(row?.[valueKey] || 0);
        const widthPct = value <= 0 ? 5 : Math.max(6, Math.min(100, (value / maxValue) * 100));
        const name = String(row?.[nameKey] || `Item ${index + 1}`);
        const rank = String(index + 1).padStart(2, '0');
        return (
          <div
            key={`${name}-${index}`}
            className="mb-4 grid grid-cols-[26px_minmax(0,1fr)_auto] items-center gap-2.5 last:mb-1"
          >
            <div className="text-[10px] font-extrabold text-[#a1acbb]">{rank}</div>
            <div className="min-w-0">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="truncate text-[11px] text-[#35435b]" title={name}>
                  {name}
                </span>
              </div>
              <div className="h-[9px] overflow-hidden rounded-full bg-[#eef2f6] shadow-[inset_0_1px_2px_rgba(15,23,42,0.035)]">
                <div
                  className={`h-full rounded-full transition-[width] duration-300 ${value <= 0 ? 'opacity-[0.18]' : ''}`}
                  style={{
                    width: `${widthPct}%`,
                    background: `linear-gradient(90deg, ${accent}, ${accent2})`,
                    boxShadow: value > 0 ? `0 2px 6px ${accent}38` : 'none',
                  }}
                />
              </div>
            </div>
            <span className="shrink-0 text-[11px] font-extrabold tabular-nums text-[#1d3150]">
              {formatValue ? formatValue(value) : value}
            </span>
          </div>
        );
      })}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className={`flex h-full min-h-0 flex-col ${className}`}>
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-[10px] font-semibold text-slate-500"
            title={sortDir === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
          >
            {sortDir === 'asc' ? <ArrowUpWideNarrow className="h-3 w-3" /> : <ArrowDownWideNarrow className="h-3 w-3" />}
            Sort
          </button>
        </div>
        <div className="min-h-0 flex-1">{rowsBlock}</div>
      </div>
    );
  }

  return (
    <section
      className={`relative flex h-[310px] flex-col overflow-hidden rounded-2xl border border-[#e5eaf0] bg-white p-5 shadow-[0_5px_20px_rgba(15,23,42,0.035)] ${className}`}
      style={{
        '--bi-accent': accent,
        '--bi-accent2': accent2,
        '--bi-wash': wash,
      }}
    >
      <div
        className="pointer-events-none absolute -right-[70px] -top-[75px] h-[180px] w-[180px] rounded-full opacity-65"
        style={{ background: wash }}
        aria-hidden
      />

      <div className="relative flex shrink-0 items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div
            className="grid h-[38px] w-[38px] place-items-center rounded-[11px] text-[17px] font-extrabold"
            style={{ background: wash, color: accent }}
          >
            {palette.icon}
          </div>
          <div>
            <h3 className="text-[14px] font-bold tracking-tight text-[#17233d]">{title}</h3>
            <p className="mt-1 text-[10px] text-[#98a3b5]">
              {sorted.length} {itemLabel} ranked
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          className="inline-flex h-[30px] items-center gap-1 rounded-lg border border-[#e4e9ef] bg-white px-2.5 text-[10px] font-semibold text-[#607087]"
          title={sortDir === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
        >
          {sortDir === 'asc' ? <ArrowUpWideNarrow className="h-3 w-3" /> : <ArrowDownWideNarrow className="h-3 w-3" />}
          Sort {sortDir === 'asc' ? '↑' : '↓'}
        </button>
      </div>

      <div className="relative mt-5 min-h-0 flex-1">{rowsBlock}</div>

      <div className="relative mt-3 flex shrink-0 items-center justify-between border-t border-[#eef1f5] pt-3 text-[10px] text-[#98a3b5]">
        <span>{footerLabel}</span>
        <strong className="font-semibold text-[#5c6b80]">
          {footerStrong || (sorted.length ? `Top ${Math.min(sorted.length, maxVisible)}` : '—')}
        </strong>
      </div>
    </section>
  );
};

export default ScrollableBarList;
